import type { CSSProperties } from "react";
import { formatValue, formatWithUnit } from "../format.ts";
import { t } from "../i18n.ts";

export interface LineEntry {
  id: string;
  name: string;
  estimate: number;
  winner: boolean;
}

// Farben der Spieler-Pins wie im Entwurf 4a: Gewinner gelb, die anderen in Pink und Violett
const OTHER_COLORS: [string, string][] = [
  ["#e0156a", "#fff"],
  ["#8a63d6", "#fff"],
  ["#ff8fb9", "#1b0b38"],
];
const WINNER_COLOR: [string, string] = ["#ffd23f", "#1b0b38"];

/** Zahlenstrahl: Antwort als cyanfarbene Marke, Schätzungen als Pins mit Abstand zur Antwort. */
export function NumberLine({ answer, unit, decimals, entries }: { answer: number; unit: string; decimals: number; entries: LineEntry[] }) {
  const values = [answer, ...entries.map((e) => e.estimate)];
  let min = Math.min(...values);
  let max = Math.max(...values);
  const pad = Math.max((max - min) * 0.14, Math.abs(answer) * 0.02, 1);
  min -= pad;
  max += pad;
  const position = (v: number) => Math.min(96, Math.max(4, ((v - min) / (max - min)) * 100));

  let other = 0;
  const colored = [...entries]
    .sort((a, b) => a.estimate - b.estimate)
    .map((entry, i) => {
      const [seg, ink] = entry.winner ? WINNER_COLOR : (OTHER_COLORS[other++ % OTHER_COLORS.length] ?? WINNER_COLOR);
      return { ...entry, seg, ink, up: i % 2 === 0, deviation: Math.abs(entry.estimate - answer) };
    });
  // Schlechteste Schätzung zuerst einblenden, der Gewinner landet zuletzt
  const order = [...colored].sort((a, b) => b.deviation - a.deviation).map((e) => e.id);
  const answerPos = position(answer);

  return (
    <div className="number-line" role="img" aria-label={`${t.answer}: ${formatWithUnit(answer, unit, decimals)}`}>
      <div className="track" />
      {[...colored]
        .sort((a, b) => b.deviation - a.deviation)
        .map((e) => {
          const p = position(e.estimate);
          return (
            <div
              key={`seg-${e.id}`}
              className="segment"
              style={{ left: `${Math.min(p, answerPos)}%`, width: `${Math.abs(p - answerPos)}%`, "--seg": e.seg } as CSSProperties}
            />
          );
        })}
      <div className="answer-mark" style={{ left: `${answerPos}%` }} />
      <div className="answer-label" style={{ left: `${answerPos}%` }}>
        {t.answer.toUpperCase()} {formatValue(answer, unit, decimals)}
      </div>
      {colored.map((e) => {
        const diff = e.estimate - answer;
        const sign = diff > 0 ? "+" : diff < 0 ? "−" : "±";
        return (
          <div
            key={e.id}
            className={`pin ${e.up ? "up" : "down"}${e.winner ? " win" : ""}`}
            style={{ left: `${position(e.estimate)}%`, animationDelay: `${order.indexOf(e.id) * 0.35}s`, "--seg": e.seg, "--seg-ink": e.ink } as CSSProperties}
          >
            <span className="dot" />
            <span className="bubble">
              <span className="who">
                {e.name} <span className="num">{formatValue(e.estimate, unit, decimals)}</span>
              </span>
              <span className="diff">
                {sign}
                {formatValue(Math.abs(diff), "", decimals)}
                {e.winner ? ` · ${t.wins}` : ""}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
